/**
 * API Key Modal Component
 *
 * Displays generated/regenerated API key details with security warning
 * Allows user to download credentials as CSV
 * Updated for API Key Rotation feature with expiry and grace period info
 */

import React from 'react';
import { X, Copy, Download, CheckCircle } from 'lucide-react';
import { Button } from 'equal-ds-ui';
import type { ApiKeyModalProps } from '../../../types/settings';

/**
 * Format date to display format (dd-MMM-yyyy)
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    // Check if date is valid (Invalid Date has NaN for getTime())
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Copy to clipboard utility
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

/**
 * CopyButton component with feedback
 */
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    // Note: Copy failure is silently ignored as it's a non-critical action
    // The user can manually select and copy the text from the input field
  };

  return (
    <button
      onClick={handleCopy}
      className="ml-2 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
      title="Copy to clipboard"
    >
      {copied ? (
        <CheckCircle className="w-4 h-4 text-green-600" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
};

export const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  apiKeyData,
  onDownloadCsv,
}) => {
  if (!isOpen || !apiKeyData) return null;

  const hasExpiry = !!apiKeyData.expires_at;
  const hasGracePeriodInfo = !!apiKeyData.grace_period_info;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 animate-slideIn">
        {/* Modal title */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">New API Key Generated</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-1"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal body */}
        <div className="px-6 py-6 space-y-4">
          {/* Service */}
          <div className="flex items-center gap-4">
            <label className="w-36 text-sm font-medium text-gray-600">
              Service
            </label>
            <div className="flex-1 flex items-center">
              <input
                type="text"
                value="Money one"
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
              />
            </div>
          </div>

          {/* Organization ID */}
          <div className="flex items-center gap-4">
            <label className="w-36 text-sm font-medium text-gray-600">
              Organization ID
            </label>
            <div className="flex-1 flex items-center">
              <input
                type="text"
                value={apiKeyData.organizationId}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900"
              />
              <CopyButton text={apiKeyData.organizationId} />
            </div>
          </div>

          {/* Client ID */}
          <div className="flex items-center gap-4">
            <label className="w-36 text-sm font-medium text-gray-600">
              Client ID
            </label>
            <div className="flex-1 flex items-center">
              <input
                type="text"
                value={apiKeyData.client_id}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-mono text-sm"
              />
              <CopyButton text={apiKeyData.client_id} />
            </div>
          </div>

          {/* Client Secret */}
          <div className="flex items-center gap-4">
            <label className="w-36 text-sm font-medium text-gray-600">
              Client Secret
            </label>
            <div className="flex-1 flex items-center">
              <input
                type="text"
                value={apiKeyData.client_secret || ''}
                readOnly
                className="flex-1 px-3 py-2 border border-pink-300 rounded-md bg-pink-50 text-gray-900 font-mono text-sm"
              />
              <CopyButton text={apiKeyData.client_secret || ''} />
            </div>
          </div>

          {/* API Key Rotation: Expiry Information */}
          {hasExpiry && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mt-4">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm text-green-900">
                    <strong>Expires:</strong> {formatDate(apiKeyData.expires_at)}
                  </p>
                  {hasGracePeriodInfo && (
                    <p className="text-sm text-green-800">
                      <strong>Previous key valid until:</strong>{' '}
                      {formatDate(apiKeyData.grace_period_info!.old_key_valid_until)}{' '}
                      ({apiKeyData.grace_period_info!.grace_days} days grace period)
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Download button */}
          <div className="flex justify-center pt-2">
            <Button
              variant="primary"
              size="md"
              onClick={onDownloadCsv}
            >
              <Download className="w-4 h-4 mr-2" />
              Download key details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
